/**
 * Shared React fiber traversal to find top-level state in a container.
 * Used by payment and direct-debit run buttons to locate table data in Impower UI state.
 *
 * @param {string} containerSelector - CSS selector for the container (e.g. '#PaymentRunOverview', '#DirectDebitOverview')
 * @param {string} searchValue - Value that must appear in the state (e.g. 'executionDate', 'sequenceType')
 * @returns {object|null} Match with { component, hookIndex, depth, value, fiber } or null
 */
function Lux_findTopLevelStateIn(containerSelector, searchValue) {
  const container = document.querySelector(containerSelector)
  if (!container) {
    console.error(`Lux Inject: Container "${containerSelector}" not found`)
    return null
  }

  let topMatch = null
  let minDepth = Infinity
  const visitedFibers = new WeakSet()

  function getReactFiber(element) {
    const fiberKey = Object.keys(element).find(k =>
      k.startsWith('__reactFiber$') ||
      k.startsWith('__reactInternalInstance$')
    )
    return fiberKey ? element[fiberKey] : null
  }

  function getFiberDepth(fiber) {
    let depth = 0
    let current = fiber
    while (current.return) {
      current = current.return
      depth++
    }
    return depth
  }

  function searchFiberHooks(fiber, search, depth) {
    let hook = fiber.memoizedState
    let hookIndex = 0

    while (hook) {
      try {
        const stateValue = hook.memoizedState
        const stateStr = JSON.stringify(stateValue)
        if (stateStr?.includes(String(search))) {
          return {
            component: fiber.type?.displayName || fiber.type?.name || 'Anonymous',
            hookIndex,
            depth,
            value: stateValue,
            fiber
          }
        }
      } catch (e) {
        // JSON.stringify can fail on circular refs
      }
      hook = hook.next
      hookIndex++
    }
    return null
  }

  for (const element of container.querySelectorAll('*')) {
    let fiber = getReactFiber(element)
    if (!fiber) continue

    let depth = getFiberDepth(fiber)

    while (fiber) {
      if (visitedFibers.has(fiber)) {
        fiber = fiber.return
        depth--
        continue
      }
      visitedFibers.add(fiber)

      if (depth < minDepth) {
        const match = searchFiberHooks(fiber, searchValue, depth)
        if (match) {
          minDepth = depth
          topMatch = match
        }
      }

      fiber = fiber.return
      depth--
    }
  }

  return topMatch
}
